'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Tiny shared state for full-height RIGHT-docked panels (currently the stock
 * chart's AI Step Log) so the LayoutShell can pad the page content right by the
 * panel's width — the panel shifts the page instead of overlaying it, the same
 * way the global watchlist rail does. The panel itself renders fixed; it just
 * publishes its width here while open (0 when closed).
 */
interface RightDockValue {
  /** Width (px) the open step-log panel occupies, or 0 when closed. */
  stepLogWidth: number;
  setStepLogWidth: (w: number) => void;
}

const RightDockContext = createContext<RightDockValue | null>(null);

export function RightDockProvider({ children }: { children: React.ReactNode }) {
  const [stepLogWidth, setStepLogWidth] = useState(0);
  const value = useMemo(() => ({ stepLogWidth, setStepLogWidth }), [stepLogWidth]);
  return <RightDockContext.Provider value={value}>{children}</RightDockContext.Provider>;
}

/** Safe no-op default so components rendered outside the provider don't crash. */
export function useRightDock(): RightDockValue {
  const ctx = useContext(RightDockContext);
  if (!ctx) return { stepLogWidth: 0, setStepLogWidth: () => {} };
  return ctx;
}

/** The panel's docked width — single source so the panel and the page-shift
 *  padding stay in sync. */
export const STEP_LOG_PANEL_WIDTH = 420;
